import type { IField, ILookup } from '@moko/utils';
import {
  FilterOperator,
  FilterType,
  type IFilterableColumn,
  type IFilterOption,
} from './filter.types';

export const CUSTOM_FIELD_KEY_PREFIX = 'otherInfo.';

export const FILTERABLE_FIELD_TYPES = new Set<string>([
  'String',
  'Number',
  'Lookup',
  'Date',
  'AutoComplete',
  'UserSelector',
  'UserGroupSelector',
  'Checkbox',
  'RadioButton',
]);

const BOOLEAN_OPTIONS: IFilterOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const LOOKUP_OPERATORS = [
  FilterOperator.Eq,
  FilterOperator.NEq,
  FilterOperator.In,
  FilterOperator.NIn,
];

type FilterShape = Pick<
  IFilterableColumn,
  'type' | 'kind' | 'operators' | 'options' | 'toggleOptions'
>;

const lookupValuesAsOptions = (
  name: string | undefined,
  lookups: readonly ILookup[] | undefined
): IFilterOption[] => {
  if (!name || !lookups?.length) return [];
  const lookup = lookups.find((l) => l.name === name);
  return (
    lookup?.values?.map((v) => ({ value: v.key, label: v.value ?? v.key })) ??
    []
  );
};

/**
 * Returns the filter column shape for a given metadata field. `Lookup` (and
 * `RadioButton` declared with a `lookupList`) becomes a select with the
 * lookup values + `Eq/NEq/In/NIn` operators; `Checkbox` and bare
 * `RadioButton` become Yes/No selects with `Eq/NEq`.
 */
export const filterShapeForField = (
  field: IField,
  lookups?: readonly ILookup[]
): FilterShape => {
  switch (field.fieldType) {
    case 'Number':
      return { type: FilterType.Number, kind: 'number' };
    case 'Date':
      return { type: FilterType.Date, kind: 'date' };
    case 'Checkbox':
      return {
        type: FilterType.String,
        kind: 'select',
        operators: [FilterOperator.Eq, FilterOperator.NEq],
        options: BOOLEAN_OPTIONS,
      };
    case 'RadioButton':
      if (field.lookupList) {
        return {
          type: FilterType.String,
          kind: 'select',
          operators: LOOKUP_OPERATORS,
          options: lookupValuesAsOptions(field.lookupList, lookups),
        };
      }
      return {
        type: FilterType.String,
        kind: 'select',
        operators: [FilterOperator.Eq, FilterOperator.NEq],
        options: BOOLEAN_OPTIONS,
      };
    case 'Lookup':
      return {
        type: FilterType.String,
        kind: 'select',
        operators: LOOKUP_OPERATORS,
        options: lookupValuesAsOptions(field.lookupList, lookups),
      };
    default:
      return { type: FilterType.String, kind: 'text' };
  }
};

export const customFieldFilterKey = (fieldName: string): string =>
  `${CUSTOM_FIELD_KEY_PREFIX}${fieldName}`;

export const buildMetadataFilterColumns = (
  fields: readonly IField[] | null | undefined,
  lookups?: readonly ILookup[]
): IFilterableColumn[] => {
  if (!fields?.length) return [];
  const seen = new Set<string>();
  const columns: IFilterableColumn[] = [];
  for (const field of fields) {
    if (
      !field?.name ||
      !FILTERABLE_FIELD_TYPES.has(field.fieldType) ||
      field.filterable !== true
    )
      continue;
    if (seen.has(field.name)) continue;
    seen.add(field.name);
    columns.push({
      key: customFieldFilterKey(field.name),
      label: field.label || field.name,
      isDefault: field.showInDefaultFilters || undefined,
      ...filterShapeForField(field, lookups),
    });
  }
  return columns;
};

/**
 * Build a single `IFilterableColumn` from a metadata field — used by callers
 * (e.g. access-rule editor) that need to drive the shared `FilterValueInput`
 * for one specific field rather than a whole grid.
 */
export const buildMetadataFilterColumn = (
  field: IField,
  lookups?: readonly ILookup[]
): IFilterableColumn => ({
  key: field.name,
  label: field.label || field.name,
  ...filterShapeForField(field, lookups),
});
